import type * as Api from '@ViewModels';
import { format } from 'date-fns';
import type { PhoneCallView } from '../../../queries/PhoneCall/usePhoneCallFilterQuery';
import {
	type CoachingAdherenceRepScore,
	MIN_CALLS_FOR_SHARE,
	type Tier,
	followedShare,
} from '../../constants/coachingAdherence';

export const MAX_RANGE_DAYS = 31;

const MS_PER_DAY = 86_400_000;

export const isNeverFollower = (rep: CoachingAdherenceRepScore): boolean =>
	rep.pastHookCalls > 0 && rep.followedCalls === 0;

export interface ScoreboardSummary {
	/** Reps with enough calls past the hook to judge. */
	rankedReps: number;
	followedOnHalf: number;
	neverFollowed: number;
	cohortShare: number | null;
	demosPerFollowedCall: number | null;
	demosPerPastHookCall: number | null;
}

const ratio = (numerator: number, denominator: number): number | null =>
	denominator > 0 ? numerator / denominator : null;

export function summarizeScoreboard(reps: CoachingAdherenceRepScore[]): ScoreboardSummary {
	const ranked = reps.filter(rep => rep.pastHookCalls >= MIN_CALLS_FOR_SHARE);
	const sum = (pick: (rep: CoachingAdherenceRepScore) => number) => reps.reduce((total, rep) => total + pick(rep), 0);
	const pastHook = sum(rep => rep.pastHookCalls);
	const followed = sum(rep => rep.followedCalls);

	return {
		rankedReps: ranked.length,
		followedOnHalf: ranked.filter(rep => (followedShare(rep) ?? 0) >= 0.5).length,
		neverFollowed: ranked.filter(isNeverFollower).length,
		cohortShare: ratio(followed, pastHook),
		demosPerFollowedCall: ratio(
			sum(rep => rep.bookedFollowedCalls),
			followed
		),
		demosPerPastHookCall: ratio(
			sum(rep => rep.bookedCalls),
			pastHook
		),
	};
}

const repCount = (count: number) => `${count} rep${count === 1 ? '' : 's'}`;

export const headlineText = ({ followedOnHalf, rankedReps, neverFollowed }: ScoreboardSummary): string =>
	`${followedOnHalf} of ${repCount(rankedReps)} followed the coach on half or more of their calls past the hook; ${neverFollowed} never did`;

/** '' means any. */
export interface RepGroupFilters {
	managerUserId: string;
	quotaTeam: string;
	role: string;
}

export const NO_GROUP_FILTERS: RepGroupFilters = { managerUserId: '', quotaTeam: '', role: '' };

export const hasGroupFilters = (filters: RepGroupFilters): boolean =>
	Boolean(filters.managerUserId || filters.quotaTeam || filters.role);

export interface GroupFilterOption {
	value: string;
	label: string;
}

const distinctOptions = (pairs: Array<[string | null | undefined, string | null | undefined]>): GroupFilterOption[] => {
	const byValue = new Map<string, string>();
	for (const [value, label] of pairs) {
		if (value && !byValue.has(value)) {
			byValue.set(value, label || value);
		}
	}
	return [...byValue].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
};

export function groupFilterOptions(reps: CoachingAdherenceRepScore[]) {
	return {
		managers: distinctOptions(reps.map(rep => [rep.managerUserId, rep.managerName])),
		quotaTeams: distinctOptions(reps.map(rep => [rep.quotaTeam, rep.quotaTeam])),
		roles: distinctOptions(reps.map(rep => [rep.role, rep.role])),
	};
}

export const applyGroupFilters = (
	reps: CoachingAdherenceRepScore[],
	filters: RepGroupFilters
): CoachingAdherenceRepScore[] =>
	reps.filter(
		rep =>
			(!filters.managerUserId || rep.managerUserId === filters.managerUserId) &&
			(!filters.quotaTeam || rep.quotaTeam === filters.quotaTeam) &&
			(!filters.role || rep.role === filters.role)
	);

export const repHeadlineText = (rep: CoachingAdherenceRepScore): string =>
	`${rep.name || rep.userId} followed the coach on ${rep.followedCalls} of ${rep.pastHookCalls} calls past the hook`;

export const formatRate = (value: number | null): string => (value == null ? 'n/a' : `${(value * 100).toFixed(1)}%`);

export const todayDateString = (now: Date = new Date()): string => format(now, 'yyyy-MM-dd');

const parseLocalDate = (value: string): Date | null => {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
		return null;
	}
	const date = new Date(`${value}T00:00:00`);
	return Number.isNaN(date.getTime()) ? null : date;
};

export type ScoreboardRange = { start: string; end: string } | { error: string };

/** The UI's end date is inclusive; the API's end is exclusive, so it gets the next local midnight. */
export function buildScoreboardRange(startDate: string, endDate: string): ScoreboardRange {
	const start = parseLocalDate(startDate);
	const lastDay = parseLocalDate(endDate);
	if (!start || !lastDay) {
		return { error: 'Enter a start and end date.' };
	}
	const end = new Date(lastDay);
	end.setDate(end.getDate() + 1);
	if (end <= start) {
		return { error: 'The end date must be on or after the start date.' };
	}
	if (end.getTime() - start.getTime() > MAX_RANGE_DAYS * MS_PER_DAY) {
		return { error: `Pick a range of ${MAX_RANGE_DAYS} days or fewer.` };
	}
	return { start: start.toISOString(), end: end.toISOString() };
}

export interface VerdictDisplay {
	label: string;
	tier: Tier;
}

export function verdictDisplay(verdict: Api.CoachingThreadVerdict): VerdictDisplay {
	switch (verdict) {
		case 'Followed':
		case 'FollowedDifferently':
			return { label: 'Took it', tier: 'good' };
		case 'Partially':
			return { label: 'Partly', tier: 'warn' };
		case 'Missed':
			return { label: 'Skipped', tier: 'bad' };
		case 'WentAgainst':
			return { label: 'Went against it', tier: 'bad' };
		default:
			return { label: 'Not counted', tier: 'neutral' };
	}
}

const TIER_PILL_COLORS = { good: 'green', warn: 'yellow', bad: 'red', neutral: 'gray' } as const satisfies Record<
	Tier,
	string
>;

export const tierPillColor = (tier: Tier) => TIER_PILL_COLORS[tier];

export const scoreTier = (score: number, followedScore: number): Tier => {
	if (score >= followedScore) {
		return 'good';
	}
	return score >= followedScore / 2 ? 'warn' : 'bad';
};

const NOT_SCORED_LABELS: Record<Api.CoachingNotScoredReason, string> = {
	NotConnected: 'Not scored: did not connect',
	NoCoaching: 'Not scored: no coaching shown',
};

export const notScoredLabel = (reason?: Api.CoachingNotScoredReason | null): string =>
	(reason && NOT_SCORED_LABELS[reason]) || 'Not scored';

const OUTCOME_LABELS: Record<string, string> = {
	HookRejected: 'Hook rejected',
	QuickPitch: 'Quick pitch',
	QualifiedPitch: 'Qualified pitch',
	Booked: 'Booked',
	Stumped: 'Stumped',
};

/** Unknown outcomes are split from PascalCase so a new server value still reads as words. */
export const loggedOutcomeLabel = (outcome?: string | null): string | null => {
	if (!outcome) {
		return null;
	}
	return OUTCOME_LABELS[outcome] ?? outcome.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export const isJudgedCall = (view: PhoneCallView): boolean => Boolean(view.phoneCall.coachingAdherenceResult?.judgment);

export const isNotScoredCall = (view: PhoneCallView): boolean =>
	Boolean(view.phoneCall.coachingAdherenceResult?.notScoredReason);

/** Calls the judge looked at, judged first; each group keeps the server's newest-first order. */
export const selectJudgeCalls = (calls: PhoneCallView[]): PhoneCallView[] => [
	...calls.filter(isJudgedCall),
	...calls.filter(call => !isJudgedCall(call) && isNotScoredCall(call)),
];
